import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Verification.module.css';
import { authService } from '../../../services/api';

const Verification = () => {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  // Get the role from location state or from the current path
  const currentPath = window.location.pathname;
  const role = location.state?.role || (currentPath.startsWith('/homeowner') ? 'homeowner' : 'user');

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    paste.split('').forEach((char, i) => {
      if (i < 6) newCode[i] = char;
    });
    setVerificationCode(newCode);
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete verification code');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.verify({
        email,
        code,
        isPasswordReset: location.state?.nextStep === 'reset-password'
      });

      // Check if this is a password reset flow
      if (location.state?.nextStep === 'reset-password') {
        navigate('/password-setup', {
          state: {
            email,
            isReset: true,
            code: code, // Pass the verification code for password reset
            role: role // Pass the role information
          }
        });
      } else if (response.data.status === 'NEEDS_PASSWORD') {
        navigate('/password-setup', {
          state: {
            email,
            isReset: false,
            role: role // Pass the role information
          }
        });
      } else if (response.data.status === 'SUCCESS') {
        localStorage.setItem('token', response.data.token);
        
        // Redirect based on role
        if (role === 'homeowner') {
          navigate('/homeowner');
        } else if (role === 'admin') {
          navigate('/admin/dashboard');
        } else {
        navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      const response = await authService.resendVerification(email);
      
      if (response.data.status === 'SENT') {
        setCountdown(120); // Reset to 2 minutes
        setVerificationCode(['', '', '', '', '', '']); // Clear inputs
      }
    } catch (error) {
      console.error('Resend error:', error);
      setError('Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBackground}>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
      </div>

      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <div className={styles.verifyIcon}>
            <i className="fas fa-envelope-open-text"></i>
          </div>
          <h2 className={styles.title}>Verify Your Email</h2>
          <p className={styles.subtitle}>
            We've sent a verification code to<br />
            <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleVerification} className={styles.verificationForm}>
          <div className={styles.codeInputGroup}>
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={styles.codeInput}
                disabled={loading}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {error && (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className={`${styles.verifyButton} ${loading ? styles.loading : ''}`}
            disabled={loading || verificationCode.join('').length !== 6}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <span>Verify Email</span>
                <i className="fas fa-check-circle"></i>
              </>
            )}
          </button>
        </form>

        <div className={styles.resendSection}>
          <div className={styles.divider}>
            <span>or</span>
          </div>
          <button
            onClick={handleResendCode}
            className={styles.resendButton}
            disabled={countdown > 0 || loading}
          >
            <i className="fas fa-redo-alt"></i>
            Resend verification code
          </button>
          {countdown > 0 && (
            <div className={styles.timer}>
              <i className="fas fa-clock"></i>
              Wait {formatTime(countdown)} to resend
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Verification; 